import { getInsiderSetting } from 'clients/MarketingClient';
import { getInsiderCartInfo } from 'clients/WebServiceClient';
import { fetchAllProvinces } from 'hooks/useProvinces';
import { CACHE_KEY, getWithExpiry, setWithExpiry } from './localStorageUtils';
import { debounce } from './timer';

export const INSIDER_PAGE_TYPE = {
  CONFIRMATION: 'Confirmation',
  OTHER: 'Other',
};

export const CUSTOMER_TYPE = {
  NEWBIE: 'Newbie', // User is actived within 7 days, do not have any order
  NO_ORDER: 'NoOrder', // User is actived > 7 days, do not have any order
  ONLY_1_ORDER: 'Only1Order', // Have 1 order only
  ONLY_2_ORDER: 'Only2Order', // Have 2 orders only
};

/**
 * @param {object} user
 * @param {string} user.createdTime
 * @param {number} user.ordersCount
 * @param {string} user.status
 * @returns {string}
 */
export function generateCustomerType(user) {
  if (!user || user.status !== 'ACTIVE' || !user.createdTime) return '';

  const createdTime = user.createdTime;
  const createdDate = new Date(createdTime);
  const currentDate = new Date();
  const diffTime = Math.abs(currentDate - createdDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (user.ordersCount === 2) return CUSTOMER_TYPE.ONLY_2_ORDER;
  if (user.ordersCount === 1) return CUSTOMER_TYPE.ONLY_1_ORDER;
  if (!user.ordersCount && diffDays > 7) return CUSTOMER_TYPE.NO_ORDER;
  if (!user.ordersCount && diffDays <= 7) return CUSTOMER_TYPE.NEWBIE;

  return '';
}

function convertPhoneNumber(phone) {
  try {
    if (!phone) {
      return null;
    }
    if (phone.startsWith('+84')) return phone;
    if (phone.startsWith('84')) return `+${phone}`;
    if (phone.startsWith('0')) return `+84${phone.substring(1, phone.length)}`;
    return `+84${phone}`;
  } catch (error) {
    console.error('Error: ', error);
  }
  return null;
}

export async function convertUser(user) {
  const province = await getProvince(user?.provinceCode);

  return {
    uuid: user?.customerID?.toString(),
    email: user?.email,
    phone_number: convertPhoneNumber(user?.phone || user?.phoneNumber),
    name: user?.name,
    gdpr_optin: true,
    email_optin: true,
    sms_optin: true,
    orders_count: user?.ordersCount || 0,
    is_activated: user?.isActive,
    custom: {
      loyalty_point: user?.point,
      thanh_pho: province?.name || '',
    },
    customer_type: user.customerType,
    customer_level: user.level,
    customer_tags: user.tags || [],
    loyalty_point: user.point,
  };
}

async function getProvince(provinceCode) {
  if (!provinceCode) return null;

  const allProvinces = await fetchAllProvinces();
  const province = allProvinces.find((p) => p.code === provinceCode);

  return province;
}

export const updateInsiderCartInfo = debounce(() => {
  if (!window?.insider_object?.user) {
    return;
  }

  getInsiderCartInfo().then((res) => {
    window.insider_object.user.cart_info = res.data;

    // console.log('data=', res.data);
    // console.table(window.insider_object.user.cart_info);
  });
}, 2 * 1000);

export async function initInsiderUser(user) {
  if (user && window && !window.insider_object) {
    const userInfo = await convertUser(user);
    window.insider_object = {
      user: userInfo,
    };
  }
}

export async function registerSuccess(user) {
  if (user && window) {
    const userInfo = await convertUser(user);

    window.insider_object = {
      user: userInfo,
      email: user?.email || '',
      phone_number: convertPhoneNumber(user?.phone || user?.phoneNumber),
      page: {
        type: 'dangky_success',
      },
    };
  }
}

export function setInsiderPageType(type) {
  if (window)
    window.insider_object = {
      ...window.insider_object,
      page: {
        type,
      },
    };
}

const TTL = 60 * 1000;
/**
 * Get insider setting from https://internal.v2-stg.thuocsi.vn/marketing/insider, then cache it
 * @returns {Promise<boolean>}
 */
export async function getCacheIsInsiderActive() {
  let isInsiderActive = getWithExpiry(CACHE_KEY.INSIDER_SETTING);
  if (isInsiderActive === null) {
    const insiderSettingRes = await getInsiderSetting();
    if (insiderSettingRes.data?.[0]) {
      isInsiderActive = insiderSettingRes.data[0].isActive || false;
      setWithExpiry(CACHE_KEY.INSIDER_SETTING, isInsiderActive, { ttl: TTL });
    }
  }

  return isInsiderActive;
}
